/**
 * Portal FAQ page content and search helpers.
 * @module content/portalFaqData
 */

import {
  CLUB4X4_BUSINESS_HOURS,
  CLUB4X4_CLAIMS_HREF,
  CLUB4X4_CUSTOMER_SUPPORT_HREF,
  CLUB4X4_FAQS_HREF,
  CLUB4X4_PHONE_DISPLAY,
  CLUB4X4_WEBSITE_HREF,
} from '../claims/contactLinks'

/** Portal URL used for FAQ hyperlinks only (dev environment). */
const PORTAL_FAQ_PORTAL_HREF = 'https://dev-portal.club4x4.com.au/'

/** Inline text, external link, or phone link within an FAQ answer. */
export type FaqSegment = string | { link: string; href: string } | { phone: string; display?: string }

/** Paragraph or bullet list block within an FAQ answer. */
export type FaqAnswerBlock =
  | { type: 'paragraph'; segments: FaqSegment[] }
  | { type: 'list'; items: FaqSegment[][] }

/** One question and answer pair. */
export interface PortalFaqItem {
  question: string
  answer: FaqAnswerBlock[]
}

/** Group of related FAQ items under a section heading. */
export interface PortalFaqSection {
  title: string
  items: PortalFaqItem[]
}

/** Build a clickable Club 4X4 support phone segment for FAQ answers. */
const phone = (): FaqSegment => ({ phone: '1300296296', display: CLUB4X4_PHONE_DISPLAY })

/** Intro copy shown above the FAQ accordion on the portal FAQ page. */
export const portalFaqIntro: FaqAnswerBlock[] = [
  {
    type: 'paragraph',
    segments: [
      'The Club 4X4 Customer Portal gives you 24/7 access to your policy, so you can view your details, make common updates, download documents, and lodge a claim online, without needing to call us during business hours.',
    ],
  },
  {
    type: 'paragraph',
    segments: [
      'For questions about your policy cover, excess, modifications, caravan and camper trailer insurance, or general claims information, please visit our ',
      { link: 'Policy & Claims FAQs', href: CLUB4X4_FAQS_HREF },
      ' on the Club 4X4 website.',
    ],
  },
]

/** FAQ sections and Q&A content for the portal FAQ page. */
export const portalFaqSections: PortalFaqSection[] = [
  {
    title: 'Getting Started',
    items: [
      {
        question: 'What is the Club 4X4 Customer Portal?',
        answer: [
          {
            type: 'paragraph',
            segments: [
              'The Club 4X4 Customer Portal is your secure, online self-service hub. It lets you view your policy details, download your policy documents, make common policy updates, update your payment details, and lodge a claim, all online, at any time of day. The portal is an additional channel alongside our existing phone service. Our team remains available if you need us.',
            ],
          },
        ],
      },
      {
        question: 'How do I access the portal?',
        answer: [
          {
            type: 'paragraph',
            segments: ['You can access the portal at ', { link: 'portal.club4x4.com.au', href: PORTAL_FAQ_PORTAL_HREF }, '.'],
          },
          {
            type: 'paragraph',
            segments: [
              "To register, you'll need to provide the following details, which must match the main driver on your policy:",
            ],
          },
          {
            type: 'list',
            items: [
              ['First and last name'],
              ['Policy number'],
              ['Email address'],
            ],
          },
          {
            type: 'paragraph',
            segments: [
              'All four details must match our records exactly. If anything doesn\'t match, please call us on ',
              phone(),
              ` (${CLUB4X4_BUSINESS_HOURS}) and we'll help you get set up.`,
            ],
          },
        ],
      },
      {
        question: 'Do I need an existing Club 4X4 login to access the portal?',
        answer: [
          {
            type: 'paragraph',
            segments: [
              'No. There is no separate Club 4X4 login credential. The portal uses its own registration process. Simply visit ',
              { link: 'portal.club4x4.com.au', href: PORTAL_FAQ_PORTAL_HREF },
              " and register using your policy details. Once registered, you'll log in with the email address and password you set during registration.",
            ],
          },
        ],
      },
      {
        question: 'Who can register for the portal?',
        answer: [
          {
            type: 'paragraph',
            segments: [
              'The portal is available to existing Club 4X4 customers only. If you hold an active policy with us, you can register using the email address associated with your policy.',
            ],
          },
          {
            type: 'paragraph',
            segments: [
              "If you're a new customer looking to get covered, you can get a quote and purchase a policy online at ",
              { link: 'club4x4.com.au', href: CLUB4X4_WEBSITE_HREF },
              ', or call our team on ',
              phone(),
              ` (${CLUB4X4_BUSINESS_HOURS}) and we'll take care of you.`,
            ],
          },
        ],
      },
      {
        question: "I've forgotten my password. What should I do?",
        answer: [
          {
            type: 'paragraph',
            segments: [
              "Select the 'Forgot password' link on the portal login page. Enter the email address registered to your policy and we'll send you a link to reset your password. If you don't receive the email within a few minutes, please check your junk or spam folder.",
            ],
          },
        ],
      },
      {
        question: 'My details have changed. Can I still register?',
        answer: [
          {
            type: 'paragraph',
            segments: [
              'Your registration details must match what we have on file for the main driver on your policy. If your name, date of birth, email address, or policy number don\'t match, registration won\'t be completed. Please call us on ',
              phone(),
              ` (${CLUB4X4_BUSINESS_HOURS}) to update your details first, then register once your records are current.`,
            ],
          },
        ],
      },
    ],
  },
  {
    title: 'What You Can Do in the Portal',
    items: [
      {
        question: 'What can I do in the portal?',
        answer: [
          {
            type: 'paragraph',
            segments: ['The portal currently supports the following tasks:'],
          },
          {
            type: 'list',
            items: [
              ['View your policy summary and details'],
              ['Download your policy documents, certificates of insurance, and tax invoices'],
              ['Update your contact details (address, phone number, email address)'],
              [
                'Make common policy changes, including updating your registration, listed drivers, accessories, and overnight parking address',
              ],
              ['View and update your payment details'],
              ['Start a claim lodgement'],
            ],
          },
          {
            type: 'paragraph',
            segments: [
              'If a task isn\'t available in the portal, our team is always happy to help over the phone on ',
              phone(),
              ` (${CLUB4X4_BUSINESS_HOURS}).`,
            ],
          },
        ],
      },
      {
        question: "What can't I do in the portal?",
        answer: [
          {
            type: 'paragraph',
            segments: [
              'The portal does not currently support all policy types or every type of request. At this stage, the following are not available online:',
            ],
          },
          {
            type: 'list',
            items: [
              ['Cancelling your policy'],
              ['Processing a renewal payment'],
              ['Making policy changes that require referral to our underwriter'],
              ['Tracking the progress of an existing claim'],
            ],
          },
          {
            type: 'paragraph',
            segments: [
              'For any of the above, please call us on ',
              phone(),
              ` (${CLUB4X4_BUSINESS_HOURS}) and our team will take care of it for you.`,
            ],
          },
        ],
      },
      {
        question: 'Can I use the portal to manage more than one policy?',
        answer: [
          {
            type: 'paragraph',
            segments: [
              'Yes. If you hold more than one policy with Club 4X4, you can manage all of your policies from the one portal account. Each policy will appear on your dashboard and can be accessed separately.',
            ],
          },
        ],
      },
      {
        question: 'Which vehicle types can be managed through the portal?',
        answer: [
          {
            type: 'paragraph',
            segments: ['The portal currently supports policies for:'],
          },
          {
            type: 'list',
            items: [['4WDs'], ['Caravans'], ['Camper trailers']],
          },
          {
            type: 'paragraph',
            segments: [
              'Support for slide-on campers and 5th wheelers is coming soon. In the meantime, if you hold one of these policies, our team is happy to assist you over the phone on ',
              phone(),
              ` (${CLUB4X4_BUSINESS_HOURS}).`,
            ],
          },
        ],
      },
    ],
  },
  {
    title: 'Viewing and Updating Your Policy',
    items: [
      {
        question: 'How do I view my policy details?',
        answer: [
          {
            type: 'paragraph',
            segments: [
              'Once you\'ve logged in, your policy summary is displayed on your home dashboard. Select your policy to view the full details, including your cover type, insured value, listed drivers, and key dates. If anything looks incorrect, you can update some details directly in the portal, or call us on ',
              phone(),
              ` (${CLUB4X4_BUSINESS_HOURS}) to make changes.`,
            ],
          },
        ],
      },
      {
        question: 'What policy changes can I make through the portal?',
        answer: [
          {
            type: 'paragraph',
            segments: ['You can make the following common policy updates directly in the portal:'],
          },
          {
            type: 'list',
            items: [
              ['Update your registration number'],
              ['Update your contact details (address, phone number, email)'],
              ['Add listed drivers'],
              ['Update your overnight parking address'],
              ['Update your accessories and modifications'],
            ],
          },
          {
            type: 'paragraph',
            segments: [
              'Most updates take effect immediately and you\'ll receive a confirmation by email. If a change requires referral to our underwriter, we\'ll be in touch to finalise the details.',
            ],
          },
        ],
      },
      {
        question: 'Will I receive confirmation when I make a change to my policy?',
        answer: [
          {
            type: 'paragraph',
            segments: [
              'Yes. Once a change is processed, we\'ll send a confirmation email to the address registered on your policy. Where a policy update or reissue is required, you\'ll also receive an updated policy schedule. If you don\'t receive a confirmation within a few minutes, check your junk or spam folder, then call us on ',
              phone(),
              ` (${CLUB4X4_BUSINESS_HOURS}) if you\'re still unsure.`,
            ],
          },
        ],
      },
      {
        question: 'Can I add a driver through the portal?',
        answer: [
          {
            type: 'paragraph',
            segments: [
              'Yes. You can add listed drivers directly in the portal. If you need to remove a driver from your policy, please call our team on ',
              phone(),
              ` (${CLUB4X4_BUSINESS_HOURS}) and we'll update this for you. Remember, all regular drivers must be listed on your policy. A regular driver is someone who drives the vehicle more than twice a month. For more information on driver requirements, visit our `,
              { link: 'Policy FAQs', href: CLUB4X4_FAQS_HREF },
              '.',
            ],
          },
        ],
      },
      {
        question: 'Can I update my payment method or frequency through the portal?',
        answer: [
          {
            type: 'paragraph',
            segments: [
              'Yes. You can view and update your payment details directly in the portal. Log in and navigate to the \'Payments\' section to make changes. For information on how payment frequency can affect your premium, visit our ',
              { link: 'Policy FAQs', href: CLUB4X4_FAQS_HREF },
              '.',
            ],
          },
        ],
      },
    ],
  },
  {
    title: 'Downloading Documents',
    items: [
      {
        question: 'How do I download my policy documents?',
        answer: [
          {
            type: 'paragraph',
            segments: [
              'Log in to the portal and navigate to the \'Documents\' section of your policy. From there you can view and download all documents associated with your policy, including your policy schedule, certificate of insurance, and tax invoices. Documents are available in PDF format and can be saved directly to your device.',
            ],
          },
        ],
      },
      {
        question: 'What documents are available in the portal?',
        answer: [
          {
            type: 'paragraph',
            segments: ['The following documents are available to download from the portal:'],
          },
          {
            type: 'list',
            items: [
              ['Policy schedule (your current cover summary)'],
              ['Certificate of insurance'],
              ['Tax invoices'],
            ],
          },
          {
            type: 'paragraph',
            segments: [
              'Your Product Disclosure Statement (PDS) is also available on the Club 4X4 website. If you need a document that isn\'t showing in the portal, please call our team on ',
              phone(),
              ` (${CLUB4X4_BUSINESS_HOURS}) and we\'ll arrange it for you.`,
            ],
          },
        ],
      },
      {
        question: "A document I need isn't showing in the portal. What should I do?",
        answer: [
          {
            type: 'paragraph',
            segments: [
              'If a document isn\'t visible in the portal, please call our team on ',
              phone(),
              ` (${CLUB4X4_BUSINESS_HOURS}) and we'll arrange it for you. If you've recently made a policy change, allow a few minutes for the updated documents to appear.`,
            ],
          },
        ],
      },
    ],
  },
  {
    title: 'Lodging a Claim Through the Portal',
    items: [
      {
        question: 'Can I lodge a claim through the portal?',
        answer: [
          {
            type: 'paragraph',
            segments: [
              'Yes. You can start a lodgement online through the portal for windscreen damage, object damage, and other loss types. Select \'Lodge a claim\' from your dashboard, choose the relevant loss type, and follow the prompts. Once submitted, our claims team will be in touch to discuss next steps. For general information about the claims process, visit our ',
              { link: 'Claims FAQs', href: CLUB4X4_CLAIMS_HREF },
              '.',
            ],
          },
        ],
      },
      {
        question: 'What information do I need to lodge a claim online?',
        answer: [
          {
            type: 'paragraph',
            segments: ['To lodge your claim through the portal, have the following ready:'],
          },
          {
            type: 'list',
            items: [
              ['Details of the incident, including the date, time, and location'],
              [
                'Details of any other people or vehicles involved, including registration numbers and contact details where available',
              ],
            ],
          },
          {
            type: 'paragraph',
            segments: [
              'Once submitted, our claims team will be in touch to guide you through any next steps, including any documentation or evidence that may be required.',
            ],
          },
        ],
      },
      {
        question: 'What happens after I lodge a claim through the portal?',
        answer: [
          {
            type: 'paragraph',
            segments: [
              'Once your claim is submitted, our claims team will review it and contact you to discuss next steps. Every claim is different. Straightforward claims like windscreens can be resolved quickly, while more complex repairs may take longer. Either way, we\'ll keep you informed throughout the process.',
            ],
          },
        ],
      },
      {
        question: "My vehicle isn't safe or driveable after an incident. Can I still lodge online?",
        answer: [
          {
            type: 'paragraph',
            segments: [
              'If your vehicle is not safe or driveable following an incident, please do not use the online portal to lodge your claim. Call our claims team directly on ',
              phone(),
              ` (${CLUB4X4_BUSINESS_HOURS}) so we can prioritise your situation and arrange the right support as quickly as possible. If you need to contact us outside of business hours, please still call our claims team directly on `,
              phone(),
              ' to access our out-of-hours service.',
            ],
          },
        ],
      },
      {
        question:
          "My vehicle isn't safe or driveable after an incident, but I'm trying to access support outside Club 4X4 office hours (8:30 am to 5:30 pm AEST). Can I still receive assistance?",
        answer: [
          {
            type: 'paragraph',
            segments: [
              'Yes, you should still call our team directly on ',
              phone(),
              ' and you will be redirected to our out-of-hours service team.',
            ],
          },
        ],
      },
      {
        question: 'Can I track my existing claim through the portal?',
        answer: [
          {
            type: 'paragraph',
            segments: [
              'Claim tracking is not yet available in the portal. If you\'d like an update on an existing claim, please call our claims team on ',
              phone(),
              ` (${CLUB4X4_BUSINESS_HOURS}) and we'll give you a full status update.`,
            ],
          },
        ],
      },
      {
        question: "What if I need to lodge a claim but can't do it online?",
        answer: [
          {
            type: 'paragraph',
            segments: [
              'If you\'re unable to complete your claim online for any reason, you can always call our claims team directly on ',
              phone(),
              ` (${CLUB4X4_BUSINESS_HOURS}). For detailed information about our claims process, what to provide, and how excess works, visit our `,
              { link: 'Claims FAQs', href: CLUB4X4_CLAIMS_HREF },
              '.',
            ],
          },
        ],
      },
    ],
  },
  {
    title: 'Getting Help',
    items: [
      {
        question: "I'm having trouble using the portal. Who can help?",
        answer: [
          {
            type: 'paragraph',
            segments: [
              'If you\'re experiencing trouble with the portal, our customer service team is happy to walk you through it. Call us on ',
              phone(),
              ` (${CLUB4X4_BUSINESS_HOURS}) and we'll assist you step by step. You can also visit our `,
              { link: 'Help & Support FAQs', href: CLUB4X4_CUSTOMER_SUPPORT_HREF },
              ' on the Club 4X4 website.',
            ],
          },
        ],
      },
      {
        question: "What if something I need to do isn't available in the portal?",
        answer: [
          {
            type: 'paragraph',
            segments: [
              'The portal covers the most common policy and claims tasks, but if you need to do something that isn\'t currently supported online, our team can help. Call us on ',
              phone(),
              ` (${CLUB4X4_BUSINESS_HOURS}) and we'll take care of it for you. The phone line remains fully open alongside the portal.`,
            ],
          },
        ],
      },
      {
        question: 'Where can I find information about my policy, cover, or claims process?',
        answer: [
          {
            type: 'paragraph',
            segments: [
              'For questions about your policy cover, excess, modifications, off-road coverage, caravan and camper trailer insurance, or how the claims process works, please visit our ',
              { link: 'Policy & Claims FAQs', href: CLUB4X4_FAQS_HREF },
              ' on the Club 4X4 website. These cover the most common questions across all of our insurance products.',
            ],
          },
        ],
      },
      {
        question: 'Is the portal secure?',
        answer: [
          {
            type: 'paragraph',
            segments: [
              'Yes. The Club 4X4 Customer Portal is built on Auth0, a leading identity and access management platform, which means your account is protected by industry-standard security. Your registration details are verified against your policy record before access is granted.',
            ],
          },
          {
            type: 'paragraph',
            segments: [
              'We recommend using a strong, unique password for your portal account and not sharing your login details with anyone. If you believe your account has been compromised, please call us on ',
              phone(),
              ` (${CLUB4X4_BUSINESS_HOURS}) as soon as possible so we can assist you.`,
            ],
          },
        ],
      },
      {
        question: 'Can I use the portal on my mobile phone or tablet?',
        answer: [
          {
            type: 'paragraph',
            segments: [
              'Yes. The portal is designed to work on desktop, tablet, and mobile devices. You can access it from any modern web browser without needing to download a separate app.',
            ],
          },
        ],
      },
    ],
  },
]

/**
 * Flatten FAQ segments and blocks into plain text for client-side search.
 *
 * @param blocks - Structured FAQ answer blocks.
 * @returns Searchable plain-text representation.
 */
export function faqAnswerBlocksToPlainText(blocks: FaqAnswerBlock[]): string {
  return blocks
    .map((block) => {
      if (block.type === 'paragraph') {
        return block.segments.map(segmentToPlainText).join('')
      }
      return block.items.map((item) => item.map(segmentToPlainText).join('')).join(' ')
    })
    .join(' ')
}

/** Convert one FAQ segment to plain text for client-side search. */
function segmentToPlainText(segment: FaqSegment): string {
  if (typeof segment === 'string') return segment
  if ('link' in segment) return segment.link
  return segment.display ?? segment.phone
}
